import { dayMonth, WEEKDAY_SHORT, type Locale } from './i18n';
import type { Repeat, Task } from './schemas';

/**
 * Calendar-day helpers. Everything works on local dates "YYYY-MM-DD" in the household's time
 * zone; no Date arithmetic across DST switches.
 */

/** Today (or the given moment) as YYYY-MM-DD in a time zone. */
export function dayIn(timeZone: string, at: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** Wall-clock time "HH:MM" in a time zone. */
export function timeIn(timeZone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(at);
}

function utcNoon(day: string): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12));
}

export function addDays(day: string, n: number): string {
  const d = utcNoon(day);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** 1 = Monday … 7 = Sunday */
export function weekdayOf(day: string): number {
  const w = utcNoon(day).getUTCDay();
  return w === 0 ? 7 : w;
}

/** First day on or after `from` that falls on one of the weekdays (1 = Mon … 7 = Sun). */
export function firstOccurrence(from: string, days: number[]): string {
  for (let i = 0; i < 7; i++) {
    const d = addDays(from, i);
    if (days.includes(weekdayOf(d))) return d;
  }
  return from;
}

/** Weekly series: is `date` one of its days? "Alle n Wochen" counts from the first real date. */
export function inWeeklySeries(
  start: string,
  repeat: { days: number[]; every?: number; until?: string; skip?: string[] },
  date: string,
): boolean {
  const first = firstOccurrence(start, repeat.days);
  return (
    date >= first &&
    // single dates taken out of the series ("Nur diesen Termin löschen", Mike 19.10.)
    !repeat.skip?.includes(date) &&
    (!repeat.until || date <= repeat.until) &&
    repeat.days.includes(weekdayOf(date)) &&
    weeksBetween(first, date) % (repeat.every ?? 1) === 0
  );
}

/** Whole weeks between the Monday-weeks of two days. */
export function weeksBetween(from: string, to: string): number {
  const monday = (d: string) => addDays(d, 1 - weekdayOf(d));
  return Math.round(daysBetween(monday(from), monday(to)) / 7);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((utcNoon(to).getTime() - utcNoon(from).getTime()) / 86_400_000);
}

export function isDueOn(repeat: Repeat, day: string): boolean {
  switch (repeat.kind) {
    case 'once':
      return repeat.date === day;
    case 'daily':
      return true;
    case 'weekly':
      return repeat.days.includes(weekdayOf(day));
    case 'flex':
      return repeat.per !== 'weekend' || weekdayOf(day) >= 6;
  }
}

/** First day of the period a flexible task belongs to: Monday, Saturday or the 1st. */
export function periodStart(per: 'week' | 'weekend' | 'month', day: string): string {
  if (per === 'month') return `${day.slice(0, 8)}01`;
  const monday = addDays(day, 1 - weekdayOf(day));
  return per === 'week' ? monday : addDays(monday, 5);
}

/**
 * The day a task's state is stored under: the day itself, or – for "1× pro Woche/Monat" – the
 * first day of its period, so one tick covers the whole period.
 */
export function instanceDate(repeat: Repeat, day: string): string {
  return repeat.kind === 'flex' ? periodStart(repeat.per, day) : day;
}

/** Is the task on today's list? Inactive tasks never are. */
export function isTaskDue(task: Pick<Task, 'repeat' | 'active'>, day: string): boolean {
  return task.active !== false && isDueOn(task.repeat, day);
}

/** Stable id of a task on a day – the first tick creates it ("wer zuerst kommt"). */
export const taskInstanceId = (taskId: string, day: string) => `${taskId}_${day}`;

/** German short weekday names – kept for callers that are German only (AI prompt). */
export const WEEKDAYS_SHORT = WEEKDAY_SHORT.de;

export const REPEAT_TEXT = {
  de: {
    daily: 'täglich',
    week: '1× pro Woche',
    weekend: '1× am Wochenende',
    month: '1× im Monat',
    once: (date: string) => `einmal am ${date}`,
    weekdays: 'Mo–Fr',
    weekends: 'am Wochenende',
  },
  en: {
    daily: 'daily',
    week: 'once a week',
    weekend: 'once at the weekend',
    month: 'once a month',
    once: (date: string) => `once on ${date}`,
    weekdays: 'Mon–Fri',
    weekends: 'at the weekend',
  },
} as const;

/** "täglich", "Mo–Fr", "Di, Do", "einmal am 3.10." */
export function describeRepeat(repeat: Repeat, locale: Locale = 'de'): string {
  const t = REPEAT_TEXT[locale];
  if (repeat.kind === 'daily') return t.daily;
  if (repeat.kind === 'flex') return t[repeat.per];
  if (repeat.kind === 'once') return t.once(dayMonth(repeat.date, locale));
  const days = [...repeat.days].sort();
  if (days.join() === '1,2,3,4,5') return t.weekdays;
  if (days.join() === '6,7') return t.weekends;
  if (days.length === 7) return t.daily;
  return days.map((d) => WEEKDAY_SHORT[locale][d - 1]).join(', ');
}

/** Minutes since midnight for "HH:MM". */
export const toMinutes = (hm: string) => {
  const [h, m] = hm.split(':').map(Number) as [number, number];
  return h * 60 + m;
};
